import { RefObject } from 'react';

import { ButtonSizes, ButtonUseType } from '@growtherapy/sprout-ui';

import {
  MeasureResponseCompletionCampaign,
  useGetMeasureValuesCallback,
  useSetMeasureErrors,
  validateMeasure,
} from '@grow-therapy-team/seedling-components';

import { useSubmitMeasureResponseCallback } from '../../hooks/measures/useSubmitMeasureResponseCallback';
import { PageComponentProps } from '../PaginatedDrawer';
import { MeasuresActions } from './MeasuresActions';
import { MeasureSchemaAndAnswers } from './types';

/** Gap (px) left between the form region's top edge and the scrolled-to error. */
const ERROR_SCROLL_MARGIN_PX = 16;

/**
 * Scrolls the first invalid measure field to the top of the given scroll
 * container. We scroll the container itself (`scrollBy`) rather than using
 * `element.scrollIntoView`, because `scrollIntoView` walks up *every* scrollable
 * ancestor — including the app shell — which shifts fixed page chrome (e.g. the
 * soft-block header / actions bars). Operating directly on the container keeps
 * the scroll fully contained to the form region. Measure field elements use
 * their field key as their DOM `id`.
 */
function scrollFirstErrorIntoView(errorFieldKey: string, container: HTMLElement) {
  const errorField =
    container.querySelector<HTMLElement>(`#${CSS.escape(errorFieldKey)}`) ?? document.getElementById(errorFieldKey);
  if (!errorField) {
    return;
  }
  const delta = errorField.getBoundingClientRect().top - container.getBoundingClientRect().top - ERROR_SCROLL_MARGIN_PX;
  container.scrollBy({ top: delta, behavior: 'smooth' });
}

type MeasuresActionsWrapperProps = PageComponentProps<MeasureSchemaAndAnswers> & {
  clientUserShortId?: string;
  providerShortId?: string;
} & {
  completionCampaign: MeasureResponseCompletionCampaign;
  onSubmitMeasureSuccess?: () => void;
  size?: ButtonSizes;
  submitButtonUse?: ButtonUseType;
  submitButtonLabel?: string;
  /** Hides the "previous" button so the flow only moves forward. */
  hidePreviousButton?: boolean;
  /**
   * When provided, validation errors are scrolled into view *within* this
   * container (`block: 'nearest'`) rather than relying on the library's default
   * scroll, which targets the document and can shift fixed page chrome (e.g.
   * the soft-block header / actions bars). Pass the form's scroll container.
   */
  errorScrollContainerRef?: RefObject<HTMLElement>;
};

export function MeasuresActionsWrapper({
  clientUserShortId,
  completionCampaign,
  page,
  providerShortId,
  isFirstPage,
  isLastPage,
  prevPage,
  nextPage,
  setPage,
  scrollToTop,
  onSubmitMeasureSuccess,
  size,
  submitButtonUse,
  submitButtonLabel,
  hidePreviousButton,
  errorScrollContainerRef,
}: MeasuresActionsWrapperProps) {
  const [submitMeasure, { isLoading }] = useSubmitMeasureResponseCallback();
  const getMeasureValues = useGetMeasureValuesCallback();
  const setMeasureErrors = useSetMeasureErrors();

  const goToNextMeasure = () => {
    if (isLastPage) {
      return;
    }
    setPage((prev) => ({ ...prev, hasSubmitted: true }));
    nextPage();
    scrollToTop();
  };

  /**
   * When a scroll container is provided, validate locally first so we can keep
   * the scroll-to-error contained to the form region. The library's submit
   * callback scrolls to the first error against the document (`block: 'start'`)
   * before throwing, which shifts the surrounding page chrome; short-circuiting
   * here avoids that while still surfacing the same inline field errors.
   */
  const handleContainedValidation = (container: HTMLElement): boolean => {
    if (!page.schema) {
      return true;
    }
    const errors = validateMeasure(getMeasureValues(), page.schema);
    if (!errors.length) {
      return true;
    }
    setMeasureErrors(errors);
    scrollFirstErrorIntoView(errors[0], container);
    return false;
  };

  const handleMeasureSubmission = async () => {
    if (errorScrollContainerRef?.current && !handleContainedValidation(errorScrollContainerRef.current)) {
      return;
    }

    const { status } = await submitMeasure({
      page,
      clientUserShortId,
      providerShortId,
      completionCampaign,
    });

    if (status !== 'success') {
      return;
    }

    onSubmitMeasureSuccess?.();
    goToNextMeasure();
  };

  const handlePreviousPage = () => {
    prevPage();
    scrollToTop();
  };

  return (
    <MeasuresActions
      isFirstPage={isFirstPage}
      isLastPage={isLastPage}
      isLoading={isLoading}
      onNextPage={handleMeasureSubmission}
      onPrevPage={handlePreviousPage}
      size={size}
      submitButtonUse={submitButtonUse}
      submitButtonLabel={submitButtonLabel}
      hidePreviousButton={hidePreviousButton}
    />
  );
}
